import { useCallback, useEffect, useMemo } from 'react';
import {
  CalculatedValueProps,
  ECalculatedValueKey,
  TCalculatedValueSetting,
} from '@timeedit/types';
import { TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { Table } from '@timeedit/ui-components/antd';
import type { ColumnsType } from '@timeedit/ui-components/antd/table/index';
import { TReportTemplateProps } from './ReportTemplate';

type TRow = { key: ECalculatedValueKey; label: string; visible: boolean };

const CalculatedValuesSettings = ({ data, onChange }: TReportTemplateProps) => {
  const applicableKeys = useMemo(
    () => getCalculatedValueKeysForTemplateType(data.templateType),
    [data.templateType]
  );

  useEffect(() => {
    if (applicableKeys.length === 0) return;
    if (data.calculatedValueSettings !== undefined) return;
    onChange(
      'calculatedValueSettings',
      createDefaultCalculatedValueSettings(data.templateType)
    );
  }, [
    applicableKeys,
    data.calculatedValueSettings,
    data.templateType,
    onChange,
  ]);

  const rows: TRow[] = useMemo(
    () =>
      applicableKeys.map((key) => ({
        key,
        label: CalculatedValueProps[key].label,
        visible: isCalculatedValueVisible(key, data.calculatedValueSettings),
      })),
    [applicableKeys, data.calculatedValueSettings]
  );

  const onToggle = useCallback(
    (key: ECalculatedValueKey, visible: boolean) => {
      const existing = data.calculatedValueSettings ?? [];
      const pruned = existing.filter(
        (s) =>
          s.key !== key &&
          CalculatedValueProps[s.key]?.templateType === data.templateType
      );
      const next: TCalculatedValueSetting[] = [...pruned, { key, visible }];
      onChange('calculatedValueSettings', next);
    },
    [data.calculatedValueSettings, data.templateType, onChange]
  );

  const columns: ColumnsType<TRow> = useMemo(
    () => [
      { title: 'Name', dataIndex: 'label', key: 'label' },
      {
        title: 'Visible',
        dataIndex: 'visible',
        key: 'visible',
        width: 100,
        align: 'center',
        render: (visible: boolean, row) => (
          <TEInput.Switch
            size="small"
            checked={visible}
            onChange={(val) => onToggle(row.key, val)}
          />
        ),
      },
    ],
    [onToggle]
  );

  if (applicableKeys.length === 0) return null;

  return (
    <TEDrawerSection label="Calculated values">
      <Table<TRow>
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="small"
      />
    </TEDrawerSection>
  );
};

export default CalculatedValuesSettings;

const getCalculatedValueKeysForTemplateType = (
  templateType: string
): ECalculatedValueKey[] => {
  return Object.entries(CalculatedValueProps)
    .filter(([_, props]) => props.templateType === templateType)
    .map(([key]) => key as ECalculatedValueKey);
};

const createDefaultCalculatedValueSettings = (
  templateType: string
): TCalculatedValueSetting[] => {
  const keys = getCalculatedValueKeysForTemplateType(templateType);
  return keys.map((key) => ({ key, visible: true }));
};

const isCalculatedValueVisible = (
  key: ECalculatedValueKey,
  settings?: TCalculatedValueSetting[]
): boolean => {
  if (!settings) return true;
  const setting = settings.find((s) => s.key === key);
  return setting ? setting.visible : true;
};
